import React from "react";

import styles from "../../styles/AudioWave/PartitionLine.module.scss";

type PartitionType = "time" | "word" | "phoneme";

const TEXT_OFFSET = 3;
const PARTITION_HEIGHTS = {
  phoneme: 130,
  time: 200,
  word: 180,
};
const TEXT_ON_TOP = -1;
const TEXT_ON_BOTTOM = 1;
const TEXT_Y_POSITION = {
  phoneme: TEXT_ON_BOTTOM,
  time: TEXT_ON_TOP,
  word: TEXT_ON_TOP,
};

/**
 * PartitionLineControl provides a line that can be dragged on the
 * AudioWave visualization to edit e.g. phonemes or audio playback
 * time.
 *
 * @param props is described below.
 * @param props.partitionType is used to apply styles to elements from PartitionLineControl.module.scss
 * @param props.initialXPosition is the current xPosition of the partition scaled to pixels (NOT 0 - 1)
 * @param props.label is the text that will be displayed with the line.
 * @param props.onLabelDoubleClick is called when the label is double clicked.
 * @param props.onPartitionLineMouseDown is called when the partition is clicked (on mouse down).
 * @param props.onPartitionLineMouseUp is called when the partition is dragged to a new location. The parameter is in the range 0 - 1 and describes the new position.
 * @param props.svgLength is the total length of the svg container, use to scale positions.
 * @param props.svgHeight is the height of the svg container in pixels
 * @param props.svgPaddingX is the amount of padding that should be accounted for, used to calculate positions.
 */
export const PartitionLine = ({
  partitionType,
  initialXPosition,
  label,
  onLabelDoubleClick,
  onPartitionLineMouseDown,
  onPartitionLineMouseUp,
  svgLength,
  svgHeight,
  svgPaddingX,
}: {
  partitionType: PartitionType;
  initialXPosition: number;
  label: string;
  onLabelDoubleClick?: () => void;
  onPartitionLineMouseDown?: () => void;
  onPartitionLineMouseUp: (xPosition: number) => void;
  svgLength: number;
  svgHeight: number;
  svgPaddingX: number;
}) => {
  const y1 = svgHeight / 2 - PARTITION_HEIGHTS[partitionType] / 2;
  const y2 = svgHeight / 2 + PARTITION_HEIGHTS[partitionType] / 2;
  const textY =
    svgHeight / 2 +
    (TEXT_Y_POSITION[partitionType] * PARTITION_HEIGHTS[partitionType]) / 2;

  const renderPartitionLine = (xPosition: number) => (
    <g
      onMouseDown={e => {
        setIsMouseDown(true);
        setXPadding(xPosition - e.clientX);
        onPartitionLineMouseDown?.();
      }}
    >
      <line
        className={styles[`${partitionType}-partition-line-group`]}
        strokeLinecap="round"
        strokeWidth="3"
        x1={xPosition + svgPaddingX}
        x2={xPosition + svgPaddingX}
        y1={y1}
        y2={y2}
      />
      <line
        className={styles["partition-line-padding"]}
        strokeLinecap="round"
        strokeWidth="12"
        x1={xPosition + svgPaddingX}
        x2={xPosition + svgPaddingX}
        y1={y1}
        y2={y2}
      />
    </g>
  );

  const renderPartitionLineText = (xPosition: number) => (
    <text
      fontSize="medium"
      onDoubleClick={onLabelDoubleClick}
      x={xPosition + svgPaddingX + TEXT_OFFSET}
      y={textY}
    >
      {label === "SIL" ? "_" : label}
    </text>
  );

  const [isMouseDown, setIsMouseDown] = React.useState(false);
  const [xPadding, setXPadding] = React.useState(0);
  const [xPosition, setXPosition] = React.useState(initialXPosition);

  const onMouseMove = React.useCallback(
    (e: MouseEvent) => {
      const newXPosition = e.clientX + xPadding;

      const clampedNewXPosition = Math.max(
        0,
        Math.min(svgLength, newXPosition),
      );

      setXPosition(clampedNewXPosition);
    },
    [xPadding, svgLength],
  );

  const onMouseUp = React.useCallback(() => {
    setIsMouseDown(false);
    onPartitionLineMouseUp(xPosition);
  }, [xPosition, onPartitionLineMouseUp]);

  React.useEffect(() => {
    if (isMouseDown) {
      window.addEventListener("mousemove", onMouseMove, false);
      window.addEventListener("mouseup", onMouseUp, false);
    }

    return () => {
      window.removeEventListener("mousemove", onMouseMove, false);
      window.removeEventListener("mouseup", onMouseUp, false);
    };
  }, [isMouseDown, onMouseMove, onMouseUp]);

  React.useEffect(() => {
    setXPosition(initialXPosition);
  }, [initialXPosition]);

  return (
    <g
      className={`${styles["partition-line-group"]} ${
        styles[`${partitionType}-partition-line`]
      }`}
    >
      {renderPartitionLineText(xPosition + svgPaddingX)}
      {renderPartitionLine(xPosition + svgPaddingX)}
    </g>
  );
};
